import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../UI/LoadingSpinner';
import { templateService } from '../../services/templateService';
import { speechService } from '../../services/speechService';
import { aiService } from '../../services/aiService';
import { webStorageService } from '../../services/webStorageService';
import { hipaaService } from '../../services/hipaaComplianceService';
import { usageTrackingService } from '../../services/usageTrackingService';

// Render a formatted note safely (no dangerouslySetInnerHTML): split on **bold**
// markers and wrap the emphasized segments in <strong>. The whitespace-pre-wrap
// container preserves line breaks, so no HTML injection is required.
const renderFormattedNote = (text) => {
  if (!text) return null;
  return text.split(/\*\*(.*?)\*\*/g).map((segment, index) =>
    index % 2 === 1
      ? <strong key={index}>{segment}</strong>
      : <React.Fragment key={index}>{segment}</React.Fragment>
  );
};

const VoiceRecorder = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Recording states
  const [recordingState, setRecordingState] = useState('idle'); // idle, recording, processing, completed, error
  const [recordingTime, setRecordingTime] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [audioLevels, setAudioLevels] = useState(new Array(20).fill(0));
  const [transcriptionText, setTranscriptionText] = useState('');
  const [liveTranscript, setLiveTranscript] = useState('');
  const [formattedNote, setFormattedNote] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [processingStep, setProcessingStep] = useState('');

  // Usage tracking states
  const [usageSummary, setUsageSummary] = useState(null);
  const [remainingTime, setRemainingTime] = useState(300); // 5 minutes in seconds
  
  // Template from dashboard or user's default template
  const [selectedTemplate, setSelectedTemplate] = useState(location.state?.templateId || '');
  const [templateInfo, setTemplateInfo] = useState(location.state?.defaultTemplate || null);
  const [usingDefaultTemplate, setUsingDefaultTemplate] = useState(!!location.state?.defaultTemplate);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  
  // Refs
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const animationRef = useRef(null);
  const finalTranscriptRef = useRef('');

  useEffect(() => {
    initializeComponent();
    loadLastCompletedNote();
    initializeUsageTracking();
    return () => cleanup();
  }, []);

  const initializeUsageTracking = async () => {
    try {
      if (user?.id) {
        usageTrackingService.setCurrentUser(user.id);
        const summary = await usageTrackingService.getUsageSummary();
        setUsageSummary(summary);
      }
    } catch (error) {
      console.error('Failed to initialize usage tracking:', error);
    }
  };

  const initializeComponent = async () => {
    // Load template info and initialize audio
    await loadTemplateInfo();
    await initializeAudio();
  };

  const loadLastCompletedNote = async () => {
    // HIPAA Compliance: Notes are no longer persisted after logout
    // Only session state is maintained for navigation between tabs
    // Notes are automatically cleared when user logs out
    console.log('Session-only note storage: No persistent notes loaded');
    return;
  };

  const saveLastCompletedNote = async (noteData) => {
    // HIPAA Compliance: Notes are kept in session only, no persistent storage
    // Notes automatically cleared when user logs out
    // This maintains notes for navigation between tabs during active session
    hipaaService.logAuditEvent({
      action: 'NOTE_GENERATED_SESSION_ONLY',
      details: `Clinical note generated for session only (${noteData.formattedNote.length} characters)`,
      level: 'SECURITY'
    });
    console.log('Note generated for current session only - no persistent storage');
  };

  const clearLastCompletedNote = async () => {
    // HIPAA Compliance: Clear session state only (no persistent storage to clear)
    // Notes are automatically cleared on logout by session management
    console.log('Cleared session note state - no persistent storage to clear');
  };

  const loadTemplateInfo = async () => {
    try {
      console.log('Loading template info for voice recorder...');
      console.log('Using default template from dashboard:', usingDefaultTemplate, templateInfo);
      
      // If coming from dashboard with a default template (SOAP, DAP, etc.), use that
      if (usingDefaultTemplate && templateInfo) {
        console.log('Using dashboard template:', templateInfo);
        setTemplateInfo(templateInfo);
        return;
      }
      
      // Otherwise, load user's custom templates. Pull from the cloud first so a
      // fresh browser/device sees the user's synced templates instead of defaults.
      const sync = await templateService.syncFromCloud();
      let userTemplates = await templateService.getUserTemplates();
      let defaultTemplate = await templateService.getDefaultTemplate();

      console.log('User templates:', userTemplates);
      console.log('Default template:', defaultTemplate);

      // Only initialize defaults when the cloud is reachable AND confirmed empty.
      // If the cloud was unreachable (sync.ok === false) we must NOT create defaults,
      // because pushing them could clobber the user's real (but unread) cloud templates.
      if (userTemplates.length === 0 && sync.ok) {
        console.log('No user templates found, initializing defaults...');
        await templateService.initializeDefaultTemplates();
        userTemplates = await templateService.getUserTemplates();
        defaultTemplate = await templateService.getDefaultTemplate();
      }
      
      // Store only user templates for dropdown (dashboard templates accessed via dashboard only)
      setAvailableTemplates(userTemplates);
      
      // Determine which template to use
      let templateToUse;
      
      // If coming from dashboard with a default template (SOAP, DAP, etc.), use that
      if (usingDefaultTemplate && templateInfo) {
        console.log('Using dashboard template:', templateInfo);
        templateToUse = templateInfo;
      } 
      // If a specific template is selected, look in user templates only
      else if (selectedTemplate && userTemplates.length > 0) {
        const specificTemplate = userTemplates.find(t => t.id === selectedTemplate);
        if (specificTemplate) {
          templateToUse = specificTemplate;
        }
      }
      // Fallback to user's default template or first available
      if (!templateToUse) {
        templateToUse = defaultTemplate || userTemplates[0];
      }
      
      if (templateToUse) {
        console.log('Using template for AI processing:', templateToUse);
        setTemplateInfo(templateToUse);
        setSelectedTemplate(templateToUse.id);
      } else {
        console.warn('No templates available, falling back to basic SOAP format');
        // Fallback template
        const fallbackTemplate = {
          id: 'fallback-soap',
          name: 'SOAP Note',
          fields: ['Subjective', 'Objective', 'Assessment', 'Plan']
        };
        setTemplateInfo(fallbackTemplate);
        setSelectedTemplate(fallbackTemplate.id);
      }
      
    } catch (error) {
      console.error('Error loading template info:', error);
      // Fallback template in case of error
      const fallbackTemplate = {
        id: 'fallback-soap',
        name: 'SOAP Note',
        fields: ['Subjective', 'Objective', 'Assessment', 'Plan']
      };
      setTemplateInfo(fallbackTemplate);
      setSelectedTemplate(fallbackTemplate.id);
    }
  };

  const initializeAudio = async () => {
    try {
      // Get available audio devices using speech service
      const deviceResult = await speechService.getAudioDevices();
      if (deviceResult.success && deviceResult.devices.length > 0) {
        setSelectedDevice(deviceResult.devices[0].deviceId);
      }
    } catch (error) {
      console.error('Failed to initialize audio:', error);
      setErrorMessage('Failed to initialize audio devices');
    }
  };

  const startRecording = async () => {
    try {
      setErrorMessage('');
      setLiveTranscript('');
      setTranscriptionText('');
      finalTranscriptRef.current = '';

      // Check usage limits before starting
      const canRecord = await usageTrackingService.canStartRecording();
      if (!canRecord.canRecord) {
        setErrorMessage('You have reached your monthly recording limit. Please contact support if you need additional recording time.');
        setRecordingState('error');
        return;
      }

      // HIPAA Compliance: Log PHI recording start
      hipaaService.logPHIAccess('CREATE', null, 'voice_recording');
      hipaaService.logAuditEvent({
        action: 'RECORDING_STARTED',
        details: `Voice recording session started for template: ${selectedTemplate}`,
        level: 'SECURITY'
      });
      
      console.log('Starting Azure Speech recognition...');
      
      // Start Azure Speech recognition
      const result = await speechService.startContinuousRecognition({
        onTranscribing: (text) => {
          // Do not log transcript content (PHI).
          setLiveTranscript(text);
        },
        onRecognized: (text) => {
          // Do not log transcript content (PHI).
          if (text.trim()) {
            finalTranscriptRef.current += text + ' ';
            setTranscriptionText(finalTranscriptRef.current.trim());
          }
          setLiveTranscript(''); // Clear live transcript after recognition
        },
        onError: (error) => {
          console.error('Speech recognition error:', error);
          setErrorMessage(`Recording error: ${error}`);
          setRecordingState('error');
          stopTimer();
        },
        onSessionStopped: () => {
          console.log('Speech recognition session stopped');
        }
      });

      if (result.success) {
        streamRef.current = result.stream;
        setRecordingState('recording');
        setRecordingTime(0);
        setRemainingTime(300); // Reset to 5 minutes
        startTimer();
        
        // Start simple audio visualization
        if (result.stream) {
          startAudioVisualization(result.stream);
        }
      } else {
        setErrorMessage(result.error || 'Failed to start recording');
        setRecordingState('error');
      }

    } catch (error) {
      console.error('Failed to start recording:', error);
      setErrorMessage('Failed to start recording. Please check your microphone permissions.');
      setRecordingState('error');
    }
  };

  const stopRecording = async () => {
    try {
      // Prevent multiple calls to stopRecording
      if (recordingState !== 'recording') {
        console.log('stopRecording called but not in recording state:', recordingState);
        return;
      }

      console.log('stopRecording: Stopping recording from state:', recordingState);

      // Stop Azure Speech recognition
      speechService.stopRecognition();

      setRecordingState('processing');
      setProcessingStep('Processing your voice recording...');

      stopTimer();
      stopAudioVisualization();

      // Track usage immediately after stopping recording
      // Use the current recordingTime before it gets cleared
      const currentRecordingTime = recordingTime;
      const recordingMinutes = Math.ceil(currentRecordingTime / 60);
      console.log(`Recording stopped after ${currentRecordingTime} seconds (${recordingMinutes} minutes)`);
      await usageTrackingService.addMinutesUsed(recordingMinutes);

      // Update usage summary in UI
      const updatedSummary = await usageTrackingService.getUsageSummary();
      setUsageSummary(updatedSummary);
      console.log('Usage tracking updated:', updatedSummary);

      // Process the final transcript
      setTimeout(() => {
        processTranscriptToNote();
      }, 1000);

    } catch (error) {
      console.error('Error stopping recording:', error);
      setErrorMessage('Error stopping recording');
      setRecordingState('error');
    }
  };

  const processTranscriptToNote = async () => {
    try {
      setProcessingStep('Converting speech to clinical note...');
      
      const finalTranscript = finalTranscriptRef.current.trim();
      
      if (!finalTranscript) {
        setErrorMessage('No speech was detected. Please try recording again.');
        setRecordingState('error');
        return;
      }
      
      // HIPAA Compliance: Log PHI processing
      hipaaService.logPHIAccess('PROCESS', null, 'clinical_transcription');
      hipaaService.logAuditEvent({
        action: 'PHI_AI_PROCESSING',
        details: `Clinical note generation started with ${finalTranscript.length} characters of transcribed audio`,
        level: 'SECURITY'
      });

      // Use AI service to format the clinical note
      console.log('VoiceRecorder: Sending template to AI:', templateInfo);
      const result = await aiService.formatClinicalNote(finalTranscript, templateInfo, {
        clientType: 'therapy client',
        sessionType: 'individual therapy'
      });
      
      if (result.success) {
        setFormattedNote(result.formattedNote);
        setRecordingState('completed');
        
        // HIPAA Compliance: Log successful clinical note generation
        hipaaService.logPHIAccess('CREATE', null, 'clinical_note');
        hipaaService.logAuditEvent({
          action: 'CLINICAL_NOTE_GENERATED',
          details: `Clinical note successfully generated (${result.formattedNote.length} characters)`,
          level: 'SECURITY'
        });
        
        // Save the completed note for persistence using secure storage
        await saveLastCompletedNote({
          formattedNote: result.formattedNote,
          transcriptionText: finalTranscript,
          templateInfo: templateInfo
        });
      } else {
        setErrorMessage(result.error || 'Failed to format clinical note');
        setRecordingState('error');
      }
      
    } catch (error) {
      console.error('Failed to process transcript:', error);
      setErrorMessage('Failed to process recording. Please try again.');
      setRecordingState('error');
    }
  };

  // Mock functions removed - now using real Azure APIs

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;

        // Update remaining time for countdown
        const remaining = Math.max(0, 300 - newTime); // 300 seconds = 5 minutes
        setRemainingTime(remaining);

        // Check if we should stop recording (5-minute limit)
        if (usageTrackingService.shouldStopRecording(newTime)) {
          console.log('5-minute limit reached, stopping recording automatically');
          // Stop the timer immediately to prevent duplicate calls
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          stopRecording();
          return newTime;
        }

        return newTime;
      });
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const startAudioVisualization = (stream) => {
    try {
      if (!stream) return;
      
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);
      analyser.fftSize = 256;

      const updateAudioLevels = () => {
        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        analyser.getByteFrequencyData(dataArray);
        
        const bars = [];
        const barCount = 20;
        const chunkSize = Math.floor(dataArray.length / barCount);
        
        for (let i = 0; i < barCount; i++) {
          let sum = 0;
          const start = i * chunkSize;
          for (let j = 0; j < chunkSize; j++) {
            sum += dataArray[start + j];
          }
          bars.push(Math.min(100, (sum / chunkSize) * 2));
        }
        
        setAudioLevels(bars);
        if (recordingState === 'recording') {
          animationRef.current = requestAnimationFrame(updateAudioLevels);
        }
      };

      updateAudioLevels();
    } catch (error) {
      console.error('Audio visualization error:', error);
    }
  };

  const stopAudioVisualization = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
  };

  const cleanup = () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      speechService.cleanup();
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    } catch (error) {
      console.error('Cleanup error:', error);
    }
  };

  const resetRecording = () => {
    setRecordingState('idle');
    setRecordingTime(0);
    setAudioLevels(new Array(20).fill(0));
    setTranscriptionText('');
    setLiveTranscript('');
    setFormattedNote('');
    setErrorMessage('');
    setProcessingStep('');
    finalTranscriptRef.current = '';
    cleanup();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(formattedNote);
      alert('Note copied to clipboard! Ready to paste into your EMR.');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy note. Please select and copy the text manually.');
    }
  };

  const startNewRecording = async () => {
    console.log('startNewRecording: Current template before reset:', templateInfo);
    await clearLastCompletedNote();
    resetRecording();
    console.log('startNewRecording: Current template after reset:', templateInfo);
    // Preserve the current template - don't reload from user defaults
    // The templateInfo state already contains the correct template
  };

  const goBackToDashboard = () => {
    navigate('/');
  };

  // Error Interface
  if (recordingState === 'error') {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Scribe Error</h1>
          </div>
          
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
            <div className="text-red-600 text-6xl mb-6">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-8">{errorMessage}</p>
            
            <div className="flex justify-center gap-6">
              <button
                onClick={resetRecording}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg px-8 py-4 rounded-xl transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // Recording Interface
  if (recordingState === 'idle' || recordingState === 'recording' || recordingState === 'processing') {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 mb-8 text-white text-center">
            <h1 className="text-3xl font-bold mb-2">Speak Your Note</h1>
            {availableTemplates.length > 0 && recordingState === 'idle' && (
              <div className="flex flex-col items-center gap-2">
                <p className="text-blue-100 text-sm font-medium">Generate any professional documentation you need in seconds</p>
                <select
                  value={selectedTemplate}
                  onChange={(e) => {
                    const newTemplate = availableTemplates.find(t => t.id === e.target.value);
                    if (newTemplate) {
                      setTemplateInfo(newTemplate);
                      setSelectedTemplate(e.target.value);
                    }
                  }}
                  className="bg-white text-gray-800 px-4 py-2 rounded-lg font-semibold min-w-48"
                >
                  {availableTemplates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

          </div>

          {/* Recording Controls */}
          <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
            
            {/* Audio Visualization */}
            <div className="flex justify-center items-center mb-8" style={{ height: '80px' }}>
              {audioLevels.map((level, index) => (
                <div
                  key={index}
                  className={`w-2 mx-1 rounded-full transition-all duration-100 ${
                    recordingState === 'recording' ? 'bg-red-500' : 'bg-gray-300'
                  }`}
                  style={{ height: `${Math.max(8, level)}%` }}
                />
              ))}
            </div>

            {/* Timer */}
            <div className="mb-8">
              <div className="text-6xl font-mono font-bold text-gray-800 mb-2">
                {formatTime(recordingTime)}
              </div>
              {recordingState === 'recording' && (
                <div className={`text-lg font-medium ${remainingTime <= 30 ? 'text-red-600' : 'text-gray-600'}`}>
                  Time remaining: {formatCountdown(remainingTime)}
                  {remainingTime <= 30 && <span className="ml-2 animate-pulse">⚠️</span>}
                </div>
              )}
            </div>

            {/* Live Transcript Display */}
            {(liveTranscript || transcriptionText) && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl max-h-32 overflow-y-auto">
                <div className="text-sm text-gray-600 mb-2">Live Transcript:</div>
                <div className="text-gray-800">
                  {transcriptionText} <span className="text-blue-600 font-semibold">{liveTranscript}</span>
                </div>
              </div>
            )}
            
            {/* Status */}
            <div className="mb-8">
              <div className={`inline-flex items-center px-6 py-3 rounded-2xl font-semibold text-lg ${
                recordingState === 'recording' ? 'bg-red-100 text-red-800' :
                recordingState === 'processing' ? 'bg-blue-100 text-blue-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {recordingState === 'recording' && 'Listening...'}
                {recordingState === 'processing' && (processingStep || 'Processing Audio...')}
                {recordingState === 'idle' && 'AI Scribe Ready'}
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex justify-center gap-6">
              {recordingState === 'idle' && (
                <button
                  onClick={startRecording}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-xl px-12 py-6 rounded-2xl transition-all duration-200 transform hover:scale-105 shadow-xl"
                >
                  START SPEAKING
                </button>
              )}

              {recordingState === 'recording' && (
                <button
                  onClick={stopRecording}
                  className="bg-red-600 hover:bg-red-700 text-white font-bold text-xl px-12 py-6 rounded-2xl transition-all duration-200 transform hover:scale-105 shadow-xl"
                >
                  STOP SPEAKING
                </button>
              )}

              {recordingState === 'processing' && (
                <div className="flex items-center justify-center">
                  <LoadingSpinner size="large" />
                  <span className="ml-4 text-xl text-gray-600">Processing your voice...</span>
                </div>
              )}
            </div>

            {recordingState === 'idle' && (
              <div className="mt-8 text-gray-500">
                <p className="mb-2">Speak naturally about what happened in your session. Retain your clinical voice.</p>
              </div>
            )}
          </div>

        </div>
      </div>
    );
  }

  // Completed Interface
  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 mb-8 text-white text-center">
          <h1 className="text-2xl font-bold mb-2">Professional Note Generated ✅</h1>
          <p className="text-blue-100">Ready to copy to your EMR system</p>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8 min-h-0">
          
          {/* Formatted Note */}
          <div className="bg-white rounded-3xl shadow-2xl p-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Progress Note Complete
            </h2>
            
            <div className="bg-gray-50 rounded-2xl p-6 mb-8 max-h-96 overflow-y-auto">
              <div className="whitespace-pre-wrap text-gray-800 text-base leading-relaxed font-sans">
                {renderFormattedNote(formattedNote)}
              </div>
            </div>

            <button
              onClick={copyToClipboard}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-xl py-4 rounded-2xl transition-all duration-200 transform hover:scale-105 shadow-xl"
            >
              COPY TO EMR
            </button>
          </div>

          {/* Actions & Transcription */}
          <div className="space-y-8 flex flex-col">
            
            {/* Quick Actions - Always Visible */}
            <div className="bg-white rounded-3xl shadow-2xl p-10 text-center flex-shrink-0">
              <h2 className="text-2xl font-bold text-gray-800 mb-8">What's Next?</h2>
              
              <div className="space-y-6">
                <button
                  onClick={startNewRecording}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold text-lg py-4 rounded-xl transition-colors"
                >
                  Speak Another Note
                </button>
              </div>
            </div>

            {/* Original Transcription - Scrollable when needed */}
            <div className="bg-white rounded-3xl shadow-2xl p-10 flex-1 min-h-0">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Your Original Words</h3>
              <div className="bg-gray-50 rounded-xl p-6 max-h-64 overflow-y-auto">
                <p className="text-gray-700 text-sm leading-relaxed">
                  {transcriptionText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoiceRecorder;